import { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Combobox } from '../../components/ui/Combobox';
import { collectGroupDescendants } from '../../lib/accountAnalytics';
import type { AccountAnalyticsRow, AmDirectoryEntry } from '../../types/accountAnalytics';
import { AmPicker } from './AmPicker';

// Stage a whole group's book (the group node + every descendant, across scope)
// onto one target AM. Uses collectGroupDescendants over the full roster, so it
// picks up accounts that aren't visible under the current operational filter —
// and tells the admin how many accounts will move before staging.
export function ReassignGroupModal({
  open,
  groups,
  rows,
  amDirectory,
  onStage,
  onClose,
}: {
  open: boolean;
  groups: Array<{ id: string; name: string }>;
  rows: AccountAnalyticsRow[];
  amDirectory: AmDirectoryEntry[];
  onStage: (accountIds: string[], amId: string | null) => void;
  onClose: () => void;
}) {
  const [groupId, setGroupId] = useState('');
  const [amId, setAmId] = useState<string | null | undefined>(undefined);

  const ids = useMemo(
    () => (groupId ? [...collectGroupDescendants(rows, groupId)] : []),
    [rows, groupId],
  );

  const reset = () => {
    setGroupId('');
    setAmId(undefined);
  };

  const stage = () => {
    if (!groupId || amId === undefined) return;
    onStage(ids, amId);
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reassign a group</DialogTitle>
          <DialogDescription>
            Move a group and its entire book (all descendant accounts, across every scope) to one
            account manager. Staged into the draft — nothing is written yet.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">Group</span>
            <Combobox
              aria-label="Group"
              placeholder="Select a group…"
              searchPlaceholder="Search groups…"
              value={groupId}
              onValueChange={setGroupId}
              options={[
                { value: '', label: 'Select a group…' },
                ...groups.map((g) => ({ value: g.id, label: g.name })),
              ]}
            />
          </div>

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">Move to</span>
            <AmPicker amDirectory={amDirectory} value={amId} onChange={setAmId} />
          </label>

          {groupId && (
            <p className="text-sm text-slate-600">
              This moves <strong>{ids.length}</strong> account{ids.length === 1 ? '' : 's'} (the
              group + all descendants).
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button onClick={stage} disabled={!groupId || amId === undefined || ids.length === 0}>
            Stage {ids.length || ''} into draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
